<script setup lang="ts">
import {
    Check,
    ChevronRight,
    CircleCheckBig,
    Eye,
    EyeOff,
    KeyRound,
    Languages,
    Palette,
    ScanLine,
    Trash2,
    X,
} from 'lucide-vue-next';
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
} from 'vue';
import { ApiError, MiniAppApi } from './api';
import BalanceHistorySheet from './components/BalanceHistorySheet.vue';
import BirthdayPicker from './components/BirthdayPicker.vue';
import BrandAvatar from './components/BrandAvatar.vue';
import DashboardContent from './components/DashboardContent.vue';
import FloatingField from './components/FloatingField.vue';
import InviteSheet from './components/InviteSheet.vue';
import LinkHero from './components/LinkHero.vue';
import LottieAnimation from './components/LottieAnimation.vue';
import LoyaltySheet from './components/LoyaltySheet.vue';
import PreviewScanner from './components/PreviewScanner.vue';
import SessionSheet from './components/SessionSheet.vue';
import TopUpSheet from './components/TopUpSheet.vue';
import WorkspaceRail from './components/WorkspaceRail.vue';
import {
    applyAccountLocale,
    applyLocale,
    initializeLocale,
    locale,
    localeOptions,
    t,
} from './i18n';
import type { MiniAppLocale } from './i18n';
import {
    createPreviewBalanceHistory,
    createPreviewBalanceHistoryPage,
    createPreviewData,
    createPreviewLoyalty,
    createPreviewReferrals,
    createPreviewTopUpApi,
    previewViews,
} from './preview';
import type {
    PreviewInvite,
    PreviewLoyalty,
    PreviewTopUp,
    PreviewView,
} from './preview';
import {
    errorScreenFor,
    intentAfterRegistration,
    referralNoteAfterJoin,
    referralNoteOnLaunch,
    referralNoteText,
    screenAfterAuthentication,
    sheetsForIntent,
} from './state';
import type { ReferralNote } from './state';
import { prepareTelegram, requestDeeplinkWriteAccess } from './telegram';
import {
    applyAccountTheme,
    persistTheme,
    readTheme,
    telegramChromeColor,
} from './theme';
import type { MiniAppTheme } from './theme';
import type {
    Account,
    BalanceHistory,
    ClubLoyalty,
    Intent,
    Network,
    ReferralOverview,
    Target,
    TelegramUser,
} from './types';

type Screen =
    | 'loading'
    | 'contact'
    | 'scan'
    | 'register'
    | 'set-password'
    | 'join'
    | 'dashboard'
    | 'error';

type RegistrationField =
    | 'first_name'
    | 'last_name'
    | 'nickname'
    | 'email'
    | 'dob'
    | 'password';
type ProfileField = Exclude<RegistrationField, 'password'>;
type ToastKind = 'success' | 'error';

const api = new MiniAppApi();
const tg = prepareTelegram();
initializeLocale(tg?.initDataUnsafe.user?.language_code);
const previewMode = import.meta.env.DEV && !tg?.initData;
const previewView = ref<PreviewView>('dashboard');
const screen = ref<Screen>('loading');
const account = ref<Account | null>(null);
const target = ref<Target | null>(null);
const telegramUser = ref<TelegramUser | null>(null);
const sessionToken = ref('');
const accessToken = ref<string | null>(null);
const registrationStage = ref<'phone' | 'profile' | null>(null);
const error = ref('');
/** What the error screen offers: a retry, or closing the Mini App (2026-10-07). */
const errorAction = ref<'retry' | 'close'>('retry');
const busy = ref(false);
/** What to say about an invitation an existing account followed (2026-10-07). */
const referralNote = ref<ReferralNote | null>(null);
const sessionSheetOpen = ref(false);
/** Why the session could not start — shown in the sheet itself, not as a toast. */
const sessionRefusal = ref<string | null>(null);
// The screen a `startapp` deep link asked for, held until the player actually reaches
// the dashboard — which for somebody with no account yet is on the far side of
// registration. Cleared the moment it is honoured, so a later visit is an ordinary one.
const intent = ref<Intent | null>(null);
const profileOpen = ref(false);
const settingsOpen = ref(false);
// «Запросити друга» (2026-10-06): read when opened, not with the dashboard — it is a list
// that grows with every friend, and most launches never look at it.
const inviteOpen = ref(false);
/** «Історія операцій» of the network on screen (2026-10-28). */
const historyOpen = ref(false);
const historyLoading = ref(false);
const balanceHistory = ref<BalanceHistory | null>(null);
/** The next, older page at the bottom of the list (2026-10-29). */
const historyNetworkId = ref<number | null>(null);
const historyLoadingMore = ref(false);
const historyMoreFailed = ref(false);
/** «Поповнити онлайн» (2026-10-27): the network whose balance is being topped up. */
const topUpOpen = ref(false);
const topUpNetworkId = ref<number | null>(null);
/** The browser preview answers the sheet itself; in Telegram it is the real API. */
const topUpApi = ref<Pick<
    MiniAppApi,
    'topUpOptions' | 'startTopUp' | 'topUpStatus'
> | null>(null);
const inviteOverview = ref<ReferralOverview | null>(null);
const inviteLoading = ref(false);
const loyaltyOpen = ref(false);
const loyaltyData = ref<ClubLoyalty | null>(null);
const loyaltyLoading = ref(false);
const loyaltyClubId = ref<number | null>(null);
const passwordSheetOpen = ref(false);
const previewScannerOpen = ref(false);
const showPassword = ref(false);
const showNewPassword = ref(false);
const showPasswordConfirmation = ref(false);
const activeNetworkId = ref<number | null>(null);
const avatarInput = ref<HTMLInputElement | null>(null);
let contactTimer: number | null = null;
let previewAvatarUrl: string | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

const registration = reactive({
    first_name: '',
    last_name: '',
    nickname: '',
    email: '',
    dob: '',
    password: '',
});
const registrationErrors = reactive<Record<RegistrationField, string>>({
    first_name: '',
    last_name: '',
    nickname: '',
    email: '',
    dob: '',
    password: '',
});
const profile = reactive({
    first_name: '',
    last_name: '',
    dob: '',
    nickname: '',
    email: '',
});
const profileErrors = reactive<Record<ProfileField, string>>({
    first_name: '',
    last_name: '',
    nickname: '',
    email: '',
    dob: '',
});
const passwordForm = reactive({ password: '', confirmation: '' });
const passwordErrors = reactive({ password: '', confirmation: '' });
const toast = reactive<{ message: string; kind: ToastKind; visible: boolean }>({
    message: '',
    kind: 'success',
    visible: false,
});

const joinedNetworks = computed<Network[]>(() =>
    (account.value?.networks ?? []).filter(
        (network) => network.clubs.length > 0,
    ),
);
const activeNetwork = computed<Network | null>(
    () =>
        joinedNetworks.value.find(
            (network) => network.id === activeNetworkId.value,
        ) ?? null,
);
const userName = computed(() => {
    const user = account.value?.profile;

    return (
        [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
        telegramUser.value?.first_name ||
        'Player'
    );
});
const userAvatar = computed(() => {
    if (account.value?.profile.avatar_source === 'none') {
        return null;
    }

    return (
        account.value?.profile.avatar_url ||
        telegramUser.value?.photo_url ||
        null
    );
});
/**
 * Fetch the avatar the moment its URL is known, not when a screen finally renders it.
 *
 * The join card is the third step of registration and the first place the picture appears
 * at any size. Mounting it started the download, so the card opened with an empty circle
 * that filled in a moment later — the page looked like it was still loading after it had
 * arrived. The URL is known much earlier: from `auth`, before the phone is even shared.
 * One `Image()` puts it in the HTTP cache and the card paints from there.
 */
watch(
    userAvatar,
    (url) => {
        if (url) {
            new Image().src = url;
        }
    },
    { immediate: true },
);

const theme = ref<MiniAppTheme>(readTheme(tg?.colorScheme));
applyTheme();
const telegramMainButtonText = computed(() =>
    screen.value === 'register'
        ? t('Створити акаунт')
        : screen.value === 'set-password'
          ? t('Зберегти пароль')
          : t('Поділитися номером телефону'),
);

/**
 * Opening the app. Split out of `onMounted` so the error screen can offer «Спробувати ще
 * раз» — landing there used to mean closing the Mini App and opening it again, which is
 * not a way out the app itself gives you.
 */
async function boot(): Promise<void> {
    error.value = '';
    screen.value = 'loading';

    try {
        if (!tg?.initData) {
            throw new Error(t('Відкрийте застосунок усередині Telegram.'));
        }

        const result = await api.authenticate(
            tg.initData,
            tg.initDataUnsafe.start_param,
        );
        sessionToken.value = result.session_token;
        accessToken.value = result.access_token;
        telegramUser.value = result.telegram_user;
        target.value = result.target;
        intent.value = result.intent;
        account.value = result.account;
        registrationStage.value = result.registration_stage;
        applyAccountPreferences(result.account);
        api.setCredentials(result.session_token, result.access_token);

        registration.first_name = result.telegram_user.first_name ?? '';
        registration.last_name = result.telegram_user.last_name ?? '';
        registration.nickname = result.telegram_user.username ?? '';
        routeAfterAuth();

        if (result.notice === 'already_joined') {
            showToast(t('Ви вже зареєстровані в цьому клубі.'), 'success');
        }

        // A standing line, not a toast: the toast went by on the way into the join screen
        // and nobody read it (Черняк, 2026-10-07).
        referralNote.value = referralNoteOnLaunch(
            result.referral_ignored ?? result.notice === 'referral_existing',
            result.notice === 'already_joined',
            result.target !== null,
        );

        requestDeeplinkWriteAccess(tg);
    } catch (caught) {
        fail(caught);
    }
}

/** The silent sign-in behind an expired access token (2026-10-07): same init data, new tokens. */
api.onExpired = async () => {
    if (!tg?.initData) {
        return false;
    }

    const result = await api.authenticate(tg.initData, undefined, true);

    sessionToken.value = result.session_token;
    accessToken.value = result.access_token;
    api.setCredentials(result.session_token, result.access_token);

    // A fresh session is enough for the session's own requests; a signed-in call needs a token.
    return true;
};

onMounted(async () => {
    applyTheme();
    tg?.MainButton?.onClick(handleTelegramMainButton);

    if (previewMode) {
        applyPreview('dashboard');

        return;
    }

    await boot();
});

onBeforeUnmount(() => {
    stopContactPolling();
    closeLoyalty();
    tg?.MainButton?.offClick(handleTelegramMainButton).hide();

    if (toastTimer) {
        clearTimeout(toastTimer);
    }

    if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
    }
});

watch(sessionSheetOpen, (open) => {
    if (open) {
        tg?.enableClosingConfirmation();
    } else {
        tg?.disableClosingConfirmation();
    }
});

watch([screen, busy], syncTelegramMainButton, { immediate: true });

function routeAfterAuth(): void {
    error.value = '';
    const destination = screenAfterAuthentication(
        account.value,
        target.value,
        registrationStage.value,
    );

    if (!account.value) {
        screen.value = destination;

        return;
    }

    activeNetworkId.value = null;
    syncProfileForm();

    if (destination === 'set-password') {
        passwordForm.password = '';
        passwordForm.confirmation = '';
        passwordErrors.password = '';
        passwordErrors.confirmation = '';
        screen.value = destination;

        return;
    }

    if (destination === 'join') {
        screen.value = destination;

        return;
    }

    if (target.value?.kind === 'computer') {
        screen.value = 'dashboard';
        // A refusal answers one press of «Почати сесію» on one target and was never
        // cleared: the sheet for every LATER scan opened already refused — «заброньовано
        // до 06:26», one button — and never asked the server again, though the seat had
        // been assigned to the player since (owner on prod, 2026-09-15).
        sessionRefusal.value = null;
        sessionSheetOpen.value = true;

        return;
    }

    screen.value = 'dashboard';
    honourIntent();
}

/**
 * Open what the deep link asked for. A player who arrived from the Shell's reset QR had
 * no account until a moment ago — registration sits between the scan and here — so this
 * runs at the end of `routeAfterAuth()`, which both the first launch and the end of
 * registration pass through.
 */
function honourIntent(): void {
    const sheets = sheetsForIntent(intent.value);
    intent.value = null;

    if (!sheets.profile) {
        return;
    }

    openProfile();

    if (sheets.password) {
        openPasswordSheet(true);
    }
}

function applyPreview(view: PreviewView): void {
    const preview = createPreviewData();

    if (view === 'empty') {
        preview.account.networks = [];
    }

    if (view === 'fallbacks') {
        preview.telegramUser.photo_url = null;
        preview.account.profile.avatar_url = null;
        preview.account.profile.avatar_source = 'none';

        for (const network of preview.account.networks) {
            network.logo_url = null;

            for (const club of network.clubs) {
                club.logo_url = null;
            }
        }
    }

    previewView.value = view;
    inviteOpen.value = false;
    inviteOverview.value = null;
    historyOpen.value = false;
    historyLoading.value = false;
    balanceHistory.value = null;
    topUpOpen.value = false;
    closeLoyalty();
    telegramUser.value = preview.telegramUser;
    account.value = preview.account;
    target.value = null;
    intent.value = null;
    activeNetworkId.value = null;
    sessionSheetOpen.value = false;
    profileOpen.value = false;
    busy.value = false;
    error.value = '';
    clearRegistrationErrors();

    // Telegram gives us the name and the username; everything below that is what the
    // person still has to fill in, so the preview leaves it empty. A pre-filled birthday
    // and password showed a form nobody ever actually sees, and hid the empty state — the
    // one where the labels sit inside the inputs and the required markers have to read.
    Object.assign(registration, {
        first_name: preview.telegramUser.first_name ?? '',
        last_name: preview.telegramUser.last_name ?? '',
        nickname: preview.telegramUser.username ?? '',
        email: '',
        dob: '',
        password: '',
    });

    if (view === 'loading') {
        screen.value = 'loading';

        return;
    }

    if (view === 'contact' || view === 'register') {
        account.value = null;
        target.value = preview.registrationTarget;
        screen.value = view === 'register' ? 'register' : 'contact';

        return;
    }

    if (view === 'set-password') {
        preview.account.profile.has_password = false;
        screen.value = 'set-password';
        passwordForm.password = '';
        passwordForm.confirmation = '';
        passwordErrors.password = '';
        passwordErrors.confirmation = '';

        return;
    }

    referralNote.value =
        view === 'join-invite-existing'
            ? 'join'
            : view === 'joined-invite-existing'
              ? 'joined'
              : null;

    if (view === 'join' || view === 'join-invite-existing') {
        target.value = preview.joinTarget;
        screen.value = 'join';

        return;
    }

    screen.value = 'dashboard';
    syncProfileForm();

    if (view === 'session') {
        target.value = preview.computerTarget;
        sessionSheetOpen.value = true;
    } else if (view === 'session-blocked') {
        target.value = preview.blockedComputerTarget;
        sessionSheetOpen.value = true;
    } else if (view === 'session-reserved') {
        target.value = preview.reservedComputerTarget;
        sessionRefusal.value = null;
        sessionSheetOpen.value = true;
    } else if (view === 'profile') {
        profileOpen.value = true;
    } else if (view.startsWith('invite')) {
        // On the network screen, where the button lives.
        const network = preview.account.networks[0];
        activeNetworkId.value = network.id;
        void openInvite(network.id);
    } else if (view.startsWith('history')) {
        // On the network screen, where the clock beside «Поповнити» lives.
        const network = preview.account.networks[0];
        activeNetworkId.value = network.id;
        void openHistory(network.id);
    } else if (view.startsWith('topup')) {
        const network = preview.account.networks[0];
        activeNetworkId.value = network.id;
        // After the close above has landed, so the sheet opens afresh.
        void nextTick(openTopUp);
    } else if (view.startsWith('loyalty')) {
        const network = preview.account.networks[0];
        activeNetworkId.value = network.id;
        void openLoyalty(network.clubs[0].id);
    } else if (view === 'password') {
        // Through the real landing rather than a lookalike, so the preview cannot drift
        // away from what the Shell's QR actually does.
        intent.value = 'password_reset';
        honourIntent();
    }
}

function selectPreview(event: Event): void {
    applyPreview((event.target as HTMLSelectElement).value as PreviewView);
}

function requestPhone(): void {
    if (previewMode) {
        applyPreview('register');

        return;
    }

    if (!tg) {
        return fail(new Error(t('Запит номера доступний лише в Telegram.')));
    }

    busy.value = true;
    tg.HapticFeedback?.impactOccurred('soft');
    tg.requestContact((accepted) => {
        if (!accepted) {
            busy.value = false;

            return;
        }

        startContactPolling();
    });
}

function handleTelegramMainButton(): void {
    if (busy.value) {
        return;
    }

    if (screen.value === 'contact') {
        requestPhone();
    } else if (screen.value === 'register') {
        void submitRegistration();
    } else if (screen.value === 'set-password') {
        void savePassword();
    }
}

function startContactPolling(): void {
    stopContactPolling();
    let attempts = 0;

    const poll = async () => {
        attempts += 1;

        try {
            const status = await api.contactStatus();

            if (status.received) {
                stopContactPolling();
                await claimOrRegister();

                return;
            }
        } catch (caught) {
            stopContactPolling();
            fail(caught);

            return;
        }

        if (attempts >= 45) {
            stopContactPolling();
            busy.value = false;
            error.value = t('Не отримали номер. Спробуйте ще раз.');

            return;
        }

        contactTimer = window.setTimeout(poll, 700);
    };

    void poll();
}

function syncTelegramMainButton(): void {
    const button = tg?.MainButton;

    if (!button) {
        return;
    }

    if (
        screen.value !== 'contact' &&
        screen.value !== 'register' &&
        screen.value !== 'set-password'
    ) {
        button.hideProgress().hide();

        return;
    }

    button.setText(telegramMainButtonText.value).show();

    if (busy.value) {
        button.disable().showProgress();
    } else {
        button.hideProgress().enable();
    }
}

function stopContactPolling(): void {
    if (contactTimer !== null) {
        window.clearTimeout(contactTimer);
        contactTimer = null;
    }
}

async function claimOrRegister(): Promise<void> {
    try {
        const result = await api.claim();
        accessToken.value = result.access_token;
        account.value = result.account;
        registrationStage.value = null;
        applyAccountPreferences(result.account);
        api.setCredentials(sessionToken.value, result.access_token);
        tg?.HapticFeedback?.notificationOccurred('success');

        routeAfterAuth();
    } catch (caught) {
        if (
            caught instanceof ApiError &&
            caught.status === 422 &&
            caught.errors.phone
        ) {
            registrationStage.value = 'profile';
            screen.value = 'register';
            error.value = '';
        } else {
            fail(caught);
        }
    } finally {
        busy.value = false;
    }
}

function clearRegistrationError(field: RegistrationField): void {
    registrationErrors[field] = '';
}

function clearRegistrationErrors(): void {
    (Object.keys(registrationErrors) as RegistrationField[]).forEach(
        (field) => {
            registrationErrors[field] = '';
        },
    );
}

function validateRegistration(): boolean {
    clearRegistrationErrors();
    registration.first_name = registration.first_name.trim();
    registration.last_name = registration.last_name.trim();
    registration.nickname = registration.nickname.trim();
    registration.email = registration.email.trim();

    if (!registration.first_name) {
        registrationErrors.first_name = t('Укажіть ім’я.');
    }

    if (
        registration.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registration.email)
    ) {
        registrationErrors.email = t('Перевірте формат email.');
    }

    if (!registration.dob) {
        registrationErrors.dob = t('Виберіть дату народження.');
    } else {
        const selectedDate = new Date(`${registration.dob}T00:00:00`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (
            Number.isNaN(selectedDate.getTime()) ||
            selectedDate.getTime() >= today.getTime()
        ) {
            registrationErrors.dob = t('Дата народження має бути в минулому.');
        }
    }

    if (registration.password.length < 8) {
        registrationErrors.password = t(
            'Пароль має містити щонайменше 8 символів.',
        );
    }

    const valid = !Object.values(registrationErrors).some(Boolean);

    if (!valid) {
        tg?.HapticFeedback?.notificationOccurred('error');
        focusFirstRegistrationError();
    }

    return valid;
}

function applyRegistrationApiErrors(caught: ApiError): boolean {
    const messages: Partial<Record<RegistrationField, string>> = {
        first_name: t('Перевірте ім’я.'),
        last_name: t('Перевірте прізвище.'),
        nickname: t('Цей нікнейм уже використовується.'),
        email: t('Цей email уже використовується.'),
        dob: t('Перевірте дату народження.'),
        password: t('Перевірте пароль.'),
    };
    let hasFieldError = false;

    (Object.keys(registrationErrors) as RegistrationField[]).forEach(
        (field) => {
            if (caught.errors[field]?.length) {
                registrationErrors[field] =
                    messages[field] ?? caught.errors[field][0];
                hasFieldError = true;
            }
        },
    );

    if (hasFieldError) {
        tg?.HapticFeedback?.notificationOccurred('error');
        focusFirstRegistrationError();
    }

    return hasFieldError;
}

function focusFirstRegistrationError(): void {
    void nextTick(() => {
        const firstInvalid = document.querySelector<HTMLElement>(
            '.registration-onboarding [aria-invalid="true"]',
        );
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus({ preventScroll: true });
    });
}

async function submitRegistration(): Promise<void> {
    error.value = '';

    if (!validateRegistration()) {
        return;
    }

    if (previewMode) {
        account.value = createPreviewData().account;
        registrationStage.value = null;
        previewView.value = target.value ? 'join' : 'dashboard';
        screen.value = target.value ? 'join' : 'dashboard';

        return;
    }

    busy.value = true;

    try {
        const result = await api.register({
            ...registration,
            email: registration.email || null,
        });
        accessToken.value = result.access_token;
        account.value = result.account;
        registrationStage.value = null;
        applyAccountPreferences(result.account);
        api.setCredentials(sessionToken.value, result.access_token);
        tg?.HapticFeedback?.notificationOccurred('success');

        intent.value = intentAfterRegistration(intent.value);

        routeAfterAuth();
    } catch (caught) {
        if (caught instanceof ApiError) {
            if (caught.status === 422 && applyRegistrationApiErrors(caught)) {
                error.value = '';
            } else {
                showError(caught);
            }
        } else {
            showError(caught);
        }
    } finally {
        busy.value = false;
    }
}

async function joinClub(): Promise<void> {
    const joinedClubName = target.value?.club.name ?? t('клуб');

    if (previewMode) {
        target.value = null;
        referralNote.value = referralNoteAfterJoin(referralNote.value);
        previewView.value = 'dashboard';
        activeNetworkId.value = null;
        screen.value = 'dashboard';
        showToast(
            t('Ви успішно приєдналися до клубу {club}.', {
                club: joinedClubName,
            }),
            'success',
        );

        return;
    }

    busy.value = true;
    error.value = '';

    try {
        account.value = await api.join();
        target.value = null;
        referralNote.value = referralNoteAfterJoin(referralNote.value);
        routeAfterAuth();
        showToast(
            t('Ви успішно приєдналися до клубу {club}.', {
                club: joinedClubName,
            }),
            'success',
        );
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        showErrorToast(caught);
    } finally {
        busy.value = false;
    }
}

/**
 * Leaving the join screen. It leaves WHATEVER the request does.
 *
 * This used to `return` out of the catch, so a failed discard — an expired session, a lost
 * connection, anything — left the player on «Приєднатися / Скасувати» with no third
 * option. A screen whose only two buttons are «do it» and «do not», where «do not» can
 * fail, is a screen with no way out.
 *
 * The cost of leaving anyway is small and self-correcting: the server may still hold the
 * pending target, so the next launch offers the same club again — which is what the player
 * asked to postpone, not something broken.
 */
async function cancelJoin(): Promise<void> {
    error.value = '';

    if (!previewMode) {
        busy.value = true;

        try {
            await api.discardTarget();
        } catch (caught) {
            showErrorToast(caught);
        }
    }

    busy.value = false;
    target.value = null;
    referralNote.value = null;
    previewView.value = 'dashboard';
    activeNetworkId.value = null;
    screen.value = 'dashboard';
}

function openDashboardHome(): void {
    error.value = '';
    profileOpen.value = false;
    settingsOpen.value = false;
    activeNetworkId.value = null;
    screen.value = 'dashboard';
}

function openSettings(): void {
    error.value = '';
    settingsOpen.value = true;
}

function selectNetwork(networkId: number): void {
    error.value = '';
    activeNetworkId.value = networkId;
}

function openScanner(): void {
    if (previewMode) {
        previewScannerOpen.value = true;

        return;
    }

    if (!tg || typeof tg.showScanQrPopup !== 'function') {
        return fail(
            new Error(
                t(
                    'Сканер QR недоступний у цій версії Telegram. Оновіть застосунок.',
                ),
            ),
        );
    }

    error.value = '';
    tg.showScanQrPopup(
        { text: t('Наведіть камеру на QR-код клубу або ПК') },
        (text) => {
            void acceptScan(text);

            return true;
        },
    );
}

function acceptPreviewScan(): void {
    previewScannerOpen.value = false;
    applyPreview('session');
}

function applyTheme(): void {
    const chromeColor = telegramChromeColor(theme.value);

    if (typeof document !== 'undefined') {
        document.documentElement.dataset.theme = theme.value;
    }

    tg?.setHeaderColor(chromeColor);
    tg?.setBackgroundColor?.(chromeColor);
}

function applyAccountPreferences(nextAccount: Account | null): void {
    applyAccountLocale(
        nextAccount?.profile.locale,
        tg?.initDataUnsafe.user?.language_code,
    );
    theme.value = applyAccountTheme(
        nextAccount?.profile.theme,
        tg?.colorScheme,
    );
    applyTheme();
}

async function setTheme(value: MiniAppTheme): Promise<void> {
    const previousTheme = theme.value;

    theme.value = value;
    applyTheme();
    persistTheme(value);

    if (previewMode && account.value) {
        account.value.profile.theme = value;

        return;
    }

    if (!account.value) {
        return;
    }

    busy.value = true;

    try {
        account.value = await api.updateProfile({ miniapp_theme: value });
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        theme.value = previousTheme;
        applyTheme();
        persistTheme(previousTheme);
        showError(caught);
    } finally {
        busy.value = false;
    }
}

async function selectLocale(nextLocale: MiniAppLocale): Promise<void> {
    const previousLocale = locale.value;

    applyLocale(nextLocale);

    if (previewMode && account.value) {
        account.value.profile.locale = nextLocale;

        return;
    }

    if (!account.value) {
        return;
    }

    busy.value = true;

    try {
        account.value = await api.updateProfile({
            miniapp_locale: nextLocale,
        });
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        applyLocale(previousLocale);
        showError(caught);
    } finally {
        busy.value = false;
    }
}

async function acceptScan(payload: string): Promise<void> {
    error.value = '';

    try {
        const scanned = await api.scan(payload);
        tg?.closeScanQrPopup();
        tg?.HapticFeedback?.notificationOccurred('success');

        // The kiosk's own «change your password» code, read with our scanner instead of
        // Telegram's camera. It points at a page, not a club, so there is nothing to join
        // and nothing to remember — just go there.
        if (scanned.kind === 'intent') {
            target.value = null;
            intent.value = scanned.intent;
            routeAfterAuth();

            return;
        }

        const scannedTarget = scanned;

        if (scannedTarget.already_joined) {
            target.value = null;
            showToast(t('Ви вже зареєстровані в цьому клубі.'), 'success');

            return;
        }

        target.value = scannedTarget;

        if (!account.value && screen.value === 'scan') {
            screen.value = 'register';
        } else {
            routeAfterAuth();
        }
    } catch (caught) {
        showError(caught);
        tg?.HapticFeedback?.notificationOccurred('error');
    }
}

async function confirmSession(): Promise<void> {
    error.value = '';
    sessionRefusal.value = null;

    if (previewMode) {
        // A booked seat is refused by the server when «Почати сесію» is pressed, and the
        // sheet stays open with the reason on screen. Reproduced here rather than faked,
        // so the preview shows the real answer and not a happier one.
        if (previewView.value === 'session-reserved') {
            sessionRefusal.value = t(
                'Цей ПК заброньовано до 14:30. Оберіть інший ПК або зверніться до адміністратора.',
            );

            return;
        }

        target.value = null;
        sessionSheetOpen.value = false;
        previewView.value = 'dashboard';
        showToast(t('Сесію успішно розпочато.'), 'success');

        return;
    }

    busy.value = true;

    try {
        await api.confirmSession();
        sessionSheetOpen.value = false;
        target.value = null;
        showToast(t('Сесію успішно розпочато.'), 'success');
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        // A refusal to start is an answer, not a hiccup: it takes over the sheet the way
        // «Доступ закрито» does, so it stays on screen and the start button goes with it.
        const reason = refusalMessage(caught);

        if (reason) {
            sessionRefusal.value = reason;
        } else {
            showErrorToast(caught);
        }
    } finally {
        busy.value = false;
    }
}

/** The server's own sentence for «this session cannot start», if that is what came back. */
function refusalMessage(caught: unknown): string | null {
    const problem = caught as {
        errors?: Record<string, string[]>;
        code?: string;
        message?: string;
    };

    const ticket = problem?.errors?.ticket?.[0];

    if (ticket) {
        return ticket;
    }

    return problem?.code === 'pc_reserved' ? (problem.message ?? null) : null;
}

async function declineSession(): Promise<void> {
    if (busy.value) {
        return;
    }

    if (previewMode) {
        target.value = null;
        sessionSheetOpen.value = false;
        previewView.value = 'dashboard';

        return;
    }

    busy.value = true;

    // The sheet closes either way. Declining tells the PC to show a fresh QR, which is
    // worth reporting when it fails — but a bottom sheet that stays put because a request
    // did is a dialog with no way out, and that is worse than an un-rotated QR. A blocked
    // player met exactly that: every exit posted a decline, the decline was refused, and
    // the sheet sat there over an error they could not read.
    try {
        await api.declineSession();
    } catch (caught) {
        // A toast, not the dashboard's inline banner: the sheet is closing over that
        // banner, so a message left there is a red line the player cannot reach or read —
        // which is how this looked when the decline was being refused. Confirming has
        // always used the toast; the two exits now report the same way.
        showErrorToast(caught);
    } finally {
        target.value = null;
        sessionSheetOpen.value = false;
        busy.value = false;
    }
}

async function openInvite(networkId: number): Promise<void> {
    error.value = '';
    inviteOverview.value = null;
    inviteOpen.value = true;

    if (previewMode) {
        inviteOverview.value = createPreviewReferrals(
            previewView.value.startsWith('invite')
                ? (previewView.value as PreviewInvite)
                : 'invite',
        );

        return;
    }

    inviteLoading.value = true;

    try {
        inviteOverview.value = await api.referrals(networkId);
    } catch (caught) {
        inviteOpen.value = false;
        showErrorToast(caught);
    } finally {
        inviteLoading.value = false;
    }
}

async function openHistory(networkId: number): Promise<void> {
    error.value = '';
    balanceHistory.value = null;
    historyNetworkId.value = networkId;
    historyMoreFailed.value = false;
    historyOpen.value = true;

    if (previewMode) {
        if (previewView.value === 'history-loading') {
            historyLoading.value = true;
        } else {
            balanceHistory.value = createPreviewBalanceHistory(
                previewView.value === 'history-empty'
                    ? 'history-empty'
                    : 'history',
            );
        }

        return;
    }

    historyLoading.value = true;

    try {
        balanceHistory.value = await api.balanceHistory(networkId);
    } catch (caught) {
        historyOpen.value = false;
        showErrorToast(caught);
    } finally {
        historyLoading.value = false;
    }
}

/** Appends the next page when the list is scrolled to its end. */
async function loadMoreHistory(): Promise<void> {
    const current = balanceHistory.value;
    const networkId = historyNetworkId.value;

    if (
        !current?.next_cursor ||
        historyLoadingMore.value ||
        networkId === null
    ) {
        return;
    }

    historyLoadingMore.value = true;
    historyMoreFailed.value = false;

    try {
        const page = previewMode
            ? await new Promise<BalanceHistory>((resolve) =>
                  window.setTimeout(
                      () =>
                          resolve(
                              createPreviewBalanceHistoryPage(
                                  current.next_cursor as string,
                              ),
                          ),
                      700,
                  ),
              )
            : await api.balanceHistory(networkId, current.next_cursor);

        // The sheet was closed or reopened meanwhile: this page belongs to nothing on screen.
        if (balanceHistory.value !== current) {
            return;
        }

        balanceHistory.value = {
            ...page,
            rows: [...current.rows, ...page.rows],
        };
    } catch {
        historyMoreFailed.value = balanceHistory.value === current;
    } finally {
        historyLoadingMore.value = false;
    }
}

/** The network's clubs that show «Квести й досягнення», for the sheet's chips. */
const loyaltyClubs = computed(
    () =>
        activeNetwork.value?.clubs.filter((c) => c.quests && !c.blocked) ?? [],
);

let loyaltyTimer: ReturnType<typeof setInterval> | null = null;

/**
 * «Квести й досягнення» of one club. Re-read every minute while the sheet is open — the
 * server keeps the board a minute and drops it the moment XP is credited — so a quest done at
 * the PC shows as done here without reopening.
 */
async function openLoyalty(clubId: number, quiet = false): Promise<void> {
    if (!quiet) {
        error.value = '';
        loyaltyData.value = null;
    }

    loyaltyClubId.value = clubId;
    loyaltyOpen.value = true;

    if (previewMode) {
        const kind = previewView.value.startsWith('loyalty')
            ? (previewView.value as PreviewLoyalty)
            : 'loyalty';

        if (kind !== 'loyalty-loading') {
            loyaltyData.value = createPreviewLoyalty(kind, clubId);
        }

        return;
    }

    loyaltyLoading.value = !quiet;

    try {
        const data = await api.loyalty(clubId);

        if (loyaltyClubId.value === clubId) {
            loyaltyData.value = data;
        }
    } catch (caught) {
        if (!quiet) {
            closeLoyalty();
            showErrorToast(caught);
        }
    } finally {
        loyaltyLoading.value = false;
    }

    if (!loyaltyTimer && loyaltyOpen.value) {
        loyaltyTimer = setInterval(() => {
            if (loyaltyOpen.value && loyaltyClubId.value) {
                void openLoyalty(loyaltyClubId.value, true);
            }
        }, 60_000);
    }
}

function closeLoyalty(): void {
    loyaltyOpen.value = false;

    if (loyaltyTimer) {
        clearInterval(loyaltyTimer);
        loyaltyTimer = null;
    }
}

/**
 * Telegram's own share sheet: the player picks a chat and the link goes with a line of text.
 * Outside Telegram (the local stand) there is no such sheet, so the link is copied instead.
 */
function shareInvite(): void {
    const link = inviteOverview.value?.link;

    if (!link) {
        return;
    }

    if (!tg?.openTelegramLink) {
        void copyInvite();

        return;
    }

    const text = t(
        'Грай зі мною в клубах Highlink! Реєструйся за моїм посиланням.',
    );

    tg.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`,
    );
}

async function copyInvite(): Promise<void> {
    const link = inviteOverview.value?.link ?? inviteOverview.value?.code;

    if (!link) {
        return;
    }

    try {
        await navigator.clipboard.writeText(link);
        tg?.HapticFeedback?.notificationOccurred('success');
        showToast(t('Посилання скопійовано.'), 'success');
    } catch {
        showToast(
            t(
                'Не вдалося скопіювати. Утримуйте посилання, щоб скопіювати його.',
            ),
            'error',
        );
    }
}

function openProfile(): void {
    error.value = '';
    settingsOpen.value = false;
    passwordSheetOpen.value = false;
    syncProfileForm();
    profileOpen.value = true;
}

function syncProfileForm(): void {
    if (!account.value) {
        return;
    }

    Object.assign(profile, {
        first_name: account.value.profile.first_name ?? '',
        last_name: account.value.profile.last_name ?? '',
        dob: account.value.profile.dob ?? '',
        nickname: account.value.profile.nickname ?? '',
        email: account.value.profile.email ?? '',
    });
    clearProfileErrors();
}

function clearProfileError(field: ProfileField): void {
    profileErrors[field] = '';
}

function clearProfileErrors(): void {
    (Object.keys(profileErrors) as ProfileField[]).forEach((field) => {
        profileErrors[field] = '';
    });
}

/**
 * «Поповнити онлайн» (owner, 2026-10-27): the sheet picks the club and the amount and opens the
 * provider's page. The browser preview answers it with its own figures (`createPreviewTopUpApi`).
 */
function openTopUp(): void {
    const network = activeNetwork.value;

    // No club of the network takes online payment (or it is off, as on production): the desk.
    if (!network || (!previewMode && !network.online_topup)) {
        explainTopUpUnavailable();

        return;
    }

    topUpApi.value = previewMode
        ? createPreviewTopUpApi(
              previewView.value.startsWith('topup')
                  ? (previewView.value as PreviewTopUp)
                  : 'topup-paid',
              network.id,
              network.clubs.map((c) => ({ id: c.id, name: c.name })),
              network.deposit,
          )
        : api;
    topUpNetworkId.value = network.id;
    topUpOpen.value = true;
}

/** The money is on the balance: the network screen shows the new figure at once. */
async function onTopUpPaid(): Promise<void> {
    tg?.HapticFeedback?.notificationOccurred('success');

    if (previewMode) {
        return;
    }

    try {
        account.value = await api.bootstrap();
    } catch {
        // The next launch reads it anyway.
    }
}

/**
 * «Поповнити» where no club of the network takes online payment: the button says to pay at the
 * desk instead of doing nothing, which reads as a broken app rather than an unfinished feature.
 */
function explainTopUpUnavailable(): void {
    showToast(
        t(
            'Поповнення поки недоступне. Будь ласка, поповніть рахунок у адміністратора.',
        ),
        'error',
    );
}

function showToast(message: string, kind: ToastKind): void {
    if (toastTimer) {
        clearTimeout(toastTimer);
    }

    toast.message = message;
    toast.kind = kind;
    toast.visible = true;
    toastTimer = setTimeout(() => {
        toast.visible = false;
        toastTimer = null;
    }, 3200);
}

function validateProfile(): boolean {
    clearProfileErrors();
    profile.first_name = profile.first_name.trim();
    profile.last_name = profile.last_name.trim();
    profile.nickname = profile.nickname.trim();
    profile.email = profile.email.trim();

    if (!profile.first_name) {
        profileErrors.first_name = t('Укажіть ім’я.');
    }

    if (!profile.dob) {
        profileErrors.dob = t('Виберіть дату народження.');
    }

    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
        profileErrors.email = t('Перевірте формат email.');
    }

    const valid = !Object.values(profileErrors).some(Boolean);

    if (!valid) {
        showToast(t('Не вдалося зберегти зміни. Перевірте поля.'), 'error');
        tg?.HapticFeedback?.notificationOccurred('error');
    }

    return valid;
}

function applyProfileApiErrors(caught: ApiError): boolean {
    const messages: Record<ProfileField, string> = {
        first_name: t('Перевірте ім’я.'),
        last_name: t('Перевірте прізвище.'),
        nickname: t('Цей нікнейм уже використовується.'),
        email: t('Цей email уже використовується.'),
        dob: t('Перевірте дату народження.'),
    };
    let hasFieldError = false;

    (Object.keys(profileErrors) as ProfileField[]).forEach((field) => {
        if (caught.errors[field]?.length) {
            profileErrors[field] = messages[field];
            hasFieldError = true;
        }
    });

    return hasFieldError;
}

async function saveProfile(): Promise<void> {
    error.value = '';

    if (!validateProfile()) {
        return;
    }

    const profileData = {
        first_name: profile.first_name,
        last_name: profile.last_name || null,
        dob: profile.dob || null,
        nickname: profile.nickname,
        email: profile.email || null,
    };

    if (previewMode && account.value) {
        Object.assign(account.value.profile, {
            first_name: profile.first_name,
            last_name: profile.last_name,
            dob: profile.dob || null,
            nickname: profile.nickname,
            email: profile.email || null,
        });
        profileOpen.value = false;
        showToast(t('Дані успішно збережено.'), 'success');

        return;
    }

    busy.value = true;

    try {
        account.value = await api.updateProfile(profileData);
        profileOpen.value = false;
        showToast(t('Дані успішно збережено.'), 'success');
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        if (caught instanceof ApiError && applyProfileApiErrors(caught)) {
            showToast(t('Не вдалося зберегти зміни. Перевірте поля.'), 'error');
        } else {
            showError(caught);
            showToast(t('Не вдалося зберегти зміни.'), 'error');
        }

        tg?.HapticFeedback?.notificationOccurred('error');
    } finally {
        busy.value = false;
    }
}

/**
 * `keepProfileBehind` is for the deep-link landing: the password form on top, the profile
 * editor underneath, so closing the form does not drop the player onto a bare dashboard.
 * Reached from the profile editor's own button it replaces that editor, as before.
 */
function openPasswordSheet(keepProfileBehind = false): void {
    profileOpen.value = keepProfileBehind;
    error.value = '';
    passwordForm.password = '';
    passwordForm.confirmation = '';
    passwordErrors.password = '';
    passwordErrors.confirmation = '';
    showNewPassword.value = false;
    showPasswordConfirmation.value = false;
    passwordSheetOpen.value = true;
}

async function savePassword(): Promise<void> {
    passwordErrors.password = '';
    passwordErrors.confirmation = '';

    if (passwordForm.password.length < 8) {
        passwordErrors.password = t(
            'Пароль має містити щонайменше 8 символів.',
        );
    }

    if (passwordForm.confirmation !== passwordForm.password) {
        passwordErrors.confirmation = t('Паролі не збігаються.');
    }

    if (passwordErrors.password || passwordErrors.confirmation) {
        showToast(t('Не вдалося оновити пароль.'), 'error');
        tg?.HapticFeedback?.notificationOccurred('error');

        return;
    }

    if (previewMode) {
        passwordSheetOpen.value = false;
        showToast(t('Пароль успішно оновлено.'), 'success');

        if (screen.value === 'set-password') {
            screen.value = 'dashboard';
        }

        return;
    }

    busy.value = true;
    error.value = '';

    try {
        account.value = await api.updateProfile({
            password: passwordForm.password,
            password_confirmation: passwordForm.confirmation,
        });
        passwordSheetOpen.value = false;
        showToast(t('Пароль успішно оновлено.'), 'success');
        tg?.HapticFeedback?.notificationOccurred('success');

        // The first password of a moved-in client: on to wherever the launch was going.
        if (screen.value === 'set-password') {
            routeAfterAuth();
        }
    } catch (caught) {
        if (caught instanceof ApiError && caught.errors.password?.length) {
            passwordErrors.password = t('Перевірте пароль.');
        } else {
            showError(caught);
        }

        showToast(t('Не вдалося оновити пароль.'), 'error');
        tg?.HapticFeedback?.notificationOccurred('error');
    } finally {
        busy.value = false;
    }
}

async function uploadAvatar(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
        return;
    }

    if (previewMode && account.value) {
        if (previewAvatarUrl) {
            URL.revokeObjectURL(previewAvatarUrl);
        }

        previewAvatarUrl = URL.createObjectURL(file);
        account.value.profile.avatar_url = previewAvatarUrl;
        (event.target as HTMLInputElement).value = '';

        return;
    }

    busy.value = true;

    try {
        account.value = await api.uploadAvatar(file);
        tg?.HapticFeedback?.notificationOccurred('success');
    } catch (caught) {
        showError(caught);
    } finally {
        busy.value = false;
        (event.target as HTMLInputElement).value = '';
    }
}

async function removeAvatar(): Promise<void> {
    if (previewMode && account.value) {
        account.value.profile.avatar_url = null;

        return;
    }

    busy.value = true;

    try {
        account.value = await api.removeAvatar();
    } catch (caught) {
        showError(caught);
    } finally {
        busy.value = false;
    }
}

function showError(caught: unknown): void {
    error.value =
        caught instanceof Error ? caught.message : t('Щось пішло не так.');
}

function showErrorToast(caught: unknown): void {
    showError(caught);
    showToast(error.value, 'error');
    tg?.HapticFeedback?.notificationOccurred('error');
}

function fail(caught: unknown): void {
    const view = errorScreenFor(caught);

    showError(caught);

    // The app's own words in the player's language for what it recognises; the server's
    // sentence otherwise.
    if (view.message) {
        error.value = t(view.message);
    }

    errorAction.value = view.action;
    screen.value = 'error';
}

/** Telegram hands out fresh init data only on a new launch, so this is the way back in. */
function closeMiniApp(): void {
    tg?.close?.();
}
</script>

<template>
    <aside v-if="previewMode" class="preview-tools">
        <span>Local preview</span>
        <select :value="previewView" @change="selectPreview">
            <option
                v-for="view in previewViews"
                :key="view.value"
                :value="view.value"
            >
                {{ view.label }}
            </option>
        </select>
    </aside>

    <Transition name="toast">
        <div
            v-if="toast.visible"
            class="mini-toast"
            :class="`mini-toast--${toast.kind}`"
            role="status"
            aria-live="polite"
        >
            <CircleCheckBig
                v-if="toast.kind === 'success'"
                :size="19"
                aria-hidden="true"
            />
            <X v-else :size="19" aria-hidden="true" />
            <span>{{ toast.message }}</span>
        </div>
    </Transition>

    <Transition name="page" mode="out-in">
        <main v-if="screen === 'loading'" class="center-screen loading-screen">
            <div class="loading-content">
                <div
                    class="cyberos-lockup loading-brand"
                    role="status"
                    :aria-label="t('Завантаження Highlink ID')"
                >
                    Highlink ID
                </div>
            </div>
        </main>

        <main v-else-if="screen === 'error'" class="center-screen page-pad">
            <div class="error-orb"><X :size="26" /></div>
            <h1>{{ t('Не вдалося відкрити Mini App') }}</h1>
            <p class="muted">{{ error }}</p>
            <!-- A dead end otherwise: whatever went wrong may well be over by now, and the
                 only alternative was closing the Mini App and opening it again. -->
            <button
                v-if="errorAction === 'close'"
                class="primary-button retry-button"
                type="button"
                data-test="error-close"
                @click="closeMiniApp"
            >
                {{ t('Закрити') }}
            </button>
            <button
                v-else
                class="primary-button retry-button"
                type="button"
                @click="boot"
            >
                {{ t('Спробувати ще раз') }}
            </button>
        </main>

        <main
            v-else-if="screen === 'contact'"
            class="onboarding contact-onboarding page-pad"
        >
            <header class="contact-topline">
                <div class="cyberos-lockup">Highlink ID</div>
            </header>
            <section class="contact-hero">
                <LottieAnimation
                    class="contact-phone-animation"
                    src="/animations/iphone.lottie"
                    :label="t('Підтвердження номера телефону')"
                />
                <div class="contact-copy">
                    <h1>{{ t('Підтвердьте номер телефону') }}</h1>
                    <p>
                        {{
                            t(
                                'Щоб автоматично передати номер телефону, натисніть кнопку внизу.',
                            )
                        }}
                    </p>
                </div>
            </section>
            <p v-if="error" class="form-error">{{ error }}</p>
        </main>

        <main
            v-else-if="screen === 'register'"
            class="onboarding registration-onboarding page-pad"
        >
            <header class="registration-topline">
                <div class="cyberos-lockup">Highlink ID</div>
            </header>
            <div class="registration-community-frame">
                <LottieAnimation
                    class="registration-community-animation"
                    src="/animations/gaming-community.lottie"
                    :loop="false"
                    :label="t('Ігрова спільнота')"
                />
            </div>
            <form
                class="form-stack"
                novalidate
                @submit.prevent="submitRegistration"
            >
                <FloatingField
                    :model-value="registration.first_name"
                    :label="t('Ім’я')"
                    required
                    autocomplete="given-name"
                    :error="registrationErrors.first_name"
                    @update:model-value="
                        registration.first_name = $event;
                        clearRegistrationError('first_name');
                    "
                />
                <FloatingField
                    :model-value="registration.last_name"
                    :label="t('Прізвище')"
                    autocomplete="family-name"
                    :error="registrationErrors.last_name"
                    @update:model-value="
                        registration.last_name = $event;
                        clearRegistrationError('last_name');
                    "
                />
                <BirthdayPicker
                    :model-value="registration.dob"
                    :error="registrationErrors.dob"
                    @update:model-value="
                        registration.dob = $event;
                        clearRegistrationError('dob');
                    "
                />
                <FloatingField
                    :model-value="registration.nickname"
                    :label="t('Нікнейм · необов’язково')"
                    :maxlength="100"
                    autocomplete="username"
                    :error="registrationErrors.nickname"
                    @update:model-value="
                        registration.nickname = $event;
                        clearRegistrationError('nickname');
                    "
                />
                <FloatingField
                    :model-value="registration.email"
                    :label="t('Email · необов’язково')"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    :error="registrationErrors.email"
                    @update:model-value="
                        registration.email = $event;
                        clearRegistrationError('email');
                    "
                />
                <FloatingField
                    :model-value="registration.password"
                    :label="t('Пароль')"
                    :type="showPassword ? 'text' : 'password'"
                    :minlength="8"
                    required
                    autocomplete="new-password"
                    :error="registrationErrors.password"
                    @update:model-value="
                        registration.password = $event;
                        clearRegistrationError('password');
                    "
                >
                    <template #trailing>
                        <button
                            type="button"
                            :aria-label="
                                showPassword
                                    ? t('Приховати пароль')
                                    : t('Показати пароль')
                            "
                            @click="showPassword = !showPassword"
                        >
                            <EyeOff v-if="showPassword" :size="19" />
                            <Eye v-else :size="19" />
                        </button>
                    </template>
                </FloatingField>
                <p v-if="error" class="form-error">{{ error }}</p>
            </form>
        </main>

        <!-- A client moved in from another club system has no password (owner,
             2026-10-05): set one before anything else. No way past it. -->
        <main
            v-else-if="screen === 'set-password'"
            class="onboarding contact-onboarding page-pad"
            data-test="set-password-screen"
        >
            <header class="contact-topline">
                <div class="cyberos-lockup">Highlink ID</div>
            </header>
            <div class="registration-community-frame">
                <LottieAnimation
                    class="registration-community-animation"
                    src="/animations/gaming-community.lottie"
                    :loop="false"
                    :label="t('Ігрова спільнота')"
                />
            </div>
            <section class="contact-hero">
                <div class="contact-copy">
                    <h1>{{ t('Придумайте пароль') }}</h1>
                    <p>
                        {{
                            t(
                                'Ваш акаунт перенесено в Highlink, баланс збережено. Пароль потрібен, щоб входити на ПК клубу за телефоном, email або нікнеймом.',
                            )
                        }}
                    </p>
                </div>
            </section>
            <form class="form-stack" novalidate @submit.prevent="savePassword">
                <FloatingField
                    v-model="passwordForm.password"
                    :label="t('Новий пароль')"
                    :type="showNewPassword ? 'text' : 'password'"
                    :minlength="8"
                    required
                    autocomplete="new-password"
                    :error="passwordErrors.password"
                    @update:model-value="passwordErrors.password = ''"
                >
                    <template #trailing>
                        <button
                            type="button"
                            :aria-label="
                                showNewPassword
                                    ? t('Приховати пароль')
                                    : t('Показати пароль')
                            "
                            @click="showNewPassword = !showNewPassword"
                        >
                            <EyeOff v-if="showNewPassword" :size="19" />
                            <Eye v-else :size="19" />
                        </button>
                    </template>
                </FloatingField>
                <FloatingField
                    v-model="passwordForm.confirmation"
                    :label="t('Повторіть новий пароль')"
                    :type="showPasswordConfirmation ? 'text' : 'password'"
                    :minlength="8"
                    required
                    autocomplete="new-password"
                    :error="passwordErrors.confirmation"
                    @update:model-value="passwordErrors.confirmation = ''"
                >
                    <template #trailing>
                        <button
                            type="button"
                            :aria-label="
                                showPasswordConfirmation
                                    ? t('Приховати пароль')
                                    : t('Показати пароль')
                            "
                            @click="
                                showPasswordConfirmation =
                                    !showPasswordConfirmation
                            "
                        >
                            <EyeOff
                                v-if="showPasswordConfirmation"
                                :size="19"
                            />
                            <Eye v-else :size="19" />
                        </button>
                    </template>
                </FloatingField>
                <p v-if="error" class="form-error">{{ error }}</p>
            </form>
        </main>

        <main v-else-if="screen === 'scan'" class="center-screen page-pad">
            <div class="success-orb"><ScanLine :size="31" /></div>
            <span class="eyebrow">{{ t('Потрібен клуб') }}</span>
            <h1>{{ t('Відскануйте QR-код') }}</h1>
            <p class="muted">
                {{
                    t(
                        'Щоб створити акаунт, спочатку відскануйте QR клубу або ПК. Так ми зрозуміємо, куди вас приєднати.',
                    )
                }}
            </p>
            <button class="primary-button" type="button" @click="openScanner">
                <ScanLine :size="19" /> {{ t('Сканувати QR-код') }}
            </button>
            <p v-if="error" class="form-error">{{ error }}</p>
        </main>

        <main
            v-else-if="screen === 'join' && target"
            class="onboarding join-onboarding page-pad"
        >
            <header class="join-topline">
                <div class="cyberos-lockup">Highlink ID</div>
            </header>
            <section class="join-center">
                <section class="copy-block join-title">
                    <h1>
                        {{
                            t('Приєднатися до {club}?', {
                                club: target.club.name,
                            })
                        }}
                    </h1>
                </section>
                <LinkHero
                    :user-avatar="userAvatar"
                    :user-name="userName"
                    :club-avatar="target.club.logo_url"
                    :club-name="target.club.name"
                />
                <section
                    class="join-access-card"
                    aria-labelledby="join-access-title"
                >
                    <p id="join-access-title">
                        {{
                            t('Це дозволить {club} отримати доступ до:', {
                                club: target.club.name,
                            })
                        }}
                    </p>
                    <ul>
                        <li>
                            <CircleCheckBig
                                class="join-access-check"
                                :size="19"
                                :stroke-width="2.15"
                                aria-hidden="true"
                            />
                            <span>{{
                                t('Вашого імені, прізвища та аватара')
                            }}</span>
                        </li>
                        <li>
                            <CircleCheckBig
                                class="join-access-check"
                                :size="19"
                                :stroke-width="2.15"
                                aria-hidden="true"
                            />
                            <span>{{ t('Вашої дати народження') }}</span>
                        </li>
                        <li>
                            <CircleCheckBig
                                class="join-access-check"
                                :size="19"
                                :stroke-width="2.15"
                                aria-hidden="true"
                            />
                            <span>{{
                                t('Вашого email і номера телефону')
                            }}</span>
                        </li>
                    </ul>
                </section>
            </section>
            <p v-if="error" class="form-error">{{ error }}</p>
            <p
                v-if="referralNote === 'join'"
                class="referral-note"
                data-test="referral-note"
                role="status"
            >
                {{ t(referralNoteText.join) }}
            </p>
            <div class="join-actions">
                <button
                    class="join-cancel-button"
                    type="button"
                    :disabled="busy"
                    @click="cancelJoin"
                >
                    {{ t('Скасувати') }}
                </button>
                <button
                    class="primary-button"
                    type="button"
                    :disabled="busy"
                    @click="joinClub"
                >
                    <span v-if="busy" class="spinner" /><span v-else>{{
                        t('Приєднатися до клубу')
                    }}</span>
                </button>
            </div>
        </main>

        <main v-else-if="screen === 'dashboard' && account" class="app-shell">
            <WorkspaceRail
                :networks="joinedNetworks"
                :avatar="userAvatar"
                :name="userName"
                :active-network-id="activeNetworkId"
                :home-active="activeNetworkId === null"
                @home="openDashboardHome"
                @select="selectNetwork"
                @settings="openSettings"
                @profile="openProfile"
            />
            <section class="workspace-content">
                <p
                    v-if="
                        referralNote === 'joined' ||
                        referralNote === 'member' ||
                        referralNote === 'existing'
                    "
                    class="referral-note referral-note-dashboard"
                    data-test="referral-note"
                    role="status"
                >
                    <span>{{ t(referralNoteText[referralNote]) }}</span>
                    <button
                        type="button"
                        class="referral-note-close"
                        :aria-label="t('Закрити')"
                        @click="referralNote = null"
                    >
                        ×
                    </button>
                </p>
                <p v-if="error" class="form-error dashboard-error">
                    {{ error }}
                </p>
                <DashboardContent
                    :profile="account.profile"
                    :networks="joinedNetworks"
                    :active-network="activeNetwork"
                    :avatar="userAvatar"
                    :fallback-first-name="telegramUser?.first_name"
                    @scan="openScanner"
                    @select-network="selectNetwork"
                    @top-up="openTopUp"
                    @edit-profile="openProfile"
                    @invite="openInvite"
                    @loyalty="openLoyalty"
                    @history="openHistory"
                />
            </section>
        </main>
    </Transition>

    <button
        v-if="
            previewMode &&
            (screen === 'contact' ||
                screen === 'register' ||
                screen === 'set-password')
        "
        class="preview-telegram-main-button"
        type="button"
        :disabled="busy"
        @click="handleTelegramMainButton"
    >
        <span v-if="busy" class="spinner" />
        <span v-else>{{ telegramMainButtonText }}</span>
    </button>

    <Transition name="sheet" appear>
        <div v-if="profileOpen && account" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="profileOpen = false"
            />
            <section class="basic-sheet profile-sheet">
                <button
                    class="sheet-close"
                    type="button"
                    @click="profileOpen = false"
                >
                    <X :size="19" />
                </button>
                <div class="avatar-editor" @click="avatarInput?.click()">
                    <BrandAvatar :src="userAvatar" :name="userName" size="lg" />
                    <button
                        v-if="userAvatar"
                        class="avatar-delete"
                        type="button"
                        :aria-label="t('Видалити фото')"
                        :disabled="busy"
                        @click.stop="removeAvatar"
                    >
                        <Trash2 :size="16" />
                    </button>
                </div>
                <input
                    ref="avatarInput"
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    @change="uploadAvatar"
                />
                <form
                    class="form-stack"
                    novalidate
                    @submit.prevent="saveProfile"
                >
                    <FloatingField
                        v-model="profile.first_name"
                        :label="t('Ім’я')"
                        required
                        :error="profileErrors.first_name"
                        @update:model-value="clearProfileError('first_name')"
                    />
                    <FloatingField
                        v-model="profile.last_name"
                        :label="t('Прізвище')"
                        :error="profileErrors.last_name"
                        @update:model-value="clearProfileError('last_name')"
                    />
                    <BirthdayPicker
                        :model-value="profile.dob"
                        :error="profileErrors.dob"
                        @update:model-value="
                            profile.dob = $event;
                            clearProfileError('dob');
                        "
                    />
                    <FloatingField
                        v-model="profile.nickname"
                        :label="t('Нікнейм · необов’язково')"
                        :error="profileErrors.nickname"
                        @update:model-value="clearProfileError('nickname')"
                    />
                    <FloatingField
                        v-model="profile.email"
                        label="Email"
                        type="email"
                        inputmode="email"
                        :error="profileErrors.email"
                        @update:model-value="clearProfileError('email')"
                    />
                    <FloatingField
                        :model-value="account.profile.phone || ''"
                        :label="t('Телефон')"
                        type="tel"
                        disabled
                    />
                    <button
                        class="profile-password-button"
                        type="button"
                        @click="openPasswordSheet()"
                    >
                        <KeyRound :size="18" />
                        <span>{{ t('Змінити пароль') }}</span>
                        <ChevronRight :size="18" />
                    </button>
                    <p v-if="error" class="form-error">{{ error }}</p>
                    <button
                        class="primary-button"
                        type="submit"
                        :disabled="busy"
                    >
                        {{ t('Зберегти') }}
                    </button>
                </form>
            </section>
        </div>
    </Transition>

    <Transition name="sheet" appear>
        <div v-if="passwordSheetOpen && account" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="passwordSheetOpen = false"
            />
            <section
                class="basic-sheet password-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="password-title"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    :disabled="busy"
                    @click="passwordSheetOpen = false"
                >
                    <X :size="19" />
                </button>
                <h2 id="password-title">{{ t('Змінити пароль') }}</h2>
                <form
                    class="form-stack"
                    novalidate
                    @submit.prevent="savePassword"
                >
                    <FloatingField
                        v-model="passwordForm.password"
                        :label="t('Новий пароль')"
                        :type="showNewPassword ? 'text' : 'password'"
                        :minlength="8"
                        required
                        autocomplete="new-password"
                        :error="passwordErrors.password"
                        @update:model-value="passwordErrors.password = ''"
                    >
                        <template #trailing>
                            <button
                                type="button"
                                :aria-label="
                                    showNewPassword
                                        ? t('Приховати пароль')
                                        : t('Показати пароль')
                                "
                                @click="showNewPassword = !showNewPassword"
                            >
                                <EyeOff v-if="showNewPassword" :size="19" />
                                <Eye v-else :size="19" />
                            </button>
                        </template>
                    </FloatingField>
                    <FloatingField
                        v-model="passwordForm.confirmation"
                        :label="t('Повторіть новий пароль')"
                        :type="showPasswordConfirmation ? 'text' : 'password'"
                        :minlength="8"
                        required
                        autocomplete="new-password"
                        :error="passwordErrors.confirmation"
                        @update:model-value="passwordErrors.confirmation = ''"
                    >
                        <template #trailing>
                            <button
                                type="button"
                                :aria-label="
                                    showPasswordConfirmation
                                        ? t('Приховати пароль')
                                        : t('Показати пароль')
                                "
                                @click="
                                    showPasswordConfirmation =
                                        !showPasswordConfirmation
                                "
                            >
                                <EyeOff
                                    v-if="showPasswordConfirmation"
                                    :size="19"
                                />
                                <Eye v-else :size="19" />
                            </button>
                        </template>
                    </FloatingField>
                    <p v-if="error" class="form-error">{{ error }}</p>
                    <button
                        class="primary-button"
                        type="submit"
                        :disabled="busy"
                    >
                        <span v-if="busy" class="spinner" />
                        <span v-else>{{ t('Оновити пароль') }}</span>
                    </button>
                </form>
            </section>
        </div>
    </Transition>

    <Transition name="sheet" appear>
        <div v-if="settingsOpen && account" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="settingsOpen = false"
            />
            <section
                class="basic-sheet settings-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="settings-title"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    @click="settingsOpen = false"
                >
                    <X :size="19" />
                </button>
                <h2 id="settings-title">{{ t('Налаштування') }}</h2>

                <section class="settings-group">
                    <div class="settings-group__heading">
                        <Languages :size="18" aria-hidden="true" />
                        <div>
                            <h3>{{ t('Мова застосунку') }}</h3>
                        </div>
                    </div>
                    <div class="settings-language-list">
                        <button
                            v-for="option in localeOptions"
                            :key="option.value"
                            type="button"
                            :class="{
                                active: option.value === locale,
                            }"
                            :disabled="busy"
                            @click="selectLocale(option.value)"
                        >
                            <span class="settings-language-code">{{
                                option.shortLabel
                            }}</span>
                            <span>{{ option.label }}</span>
                            <Check
                                v-if="option.value === locale"
                                :size="18"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </section>

                <section class="settings-group">
                    <div class="settings-group__heading">
                        <Palette :size="18" aria-hidden="true" />
                        <div>
                            <h3>{{ t('Тема') }}</h3>
                        </div>
                    </div>
                    <div class="settings-theme-switcher">
                        <button
                            type="button"
                            :class="{ active: theme === 'light' }"
                            :disabled="busy"
                            @click="setTheme('light')"
                        >
                            {{ t('Світла') }}
                        </button>
                        <button
                            type="button"
                            :class="{ active: theme === 'dark' }"
                            :disabled="busy"
                            @click="setTheme('dark')"
                        >
                            {{ t('Темна') }}
                        </button>
                    </div>
                </section>
            </section>
        </div>
    </Transition>

    <LoyaltySheet
        :open="loyaltyOpen"
        :data="loyaltyData"
        :loading="loyaltyLoading"
        :clubs="loyaltyClubs"
        :club-id="loyaltyClubId"
        @close="closeLoyalty"
        @select-club="openLoyalty"
    />

    <BalanceHistorySheet
        :open="historyOpen"
        :network="activeNetwork?.name ?? null"
        :history="balanceHistory"
        :loading="historyLoading"
        :loading-more="historyLoadingMore"
        :more-failed="historyMoreFailed"
        @more="loadMoreHistory"
        @close="historyOpen = false"
    />

    <InviteSheet
        :open="inviteOpen"
        :overview="inviteOverview"
        :loading="inviteLoading"
        @close="inviteOpen = false"
        @share="shareInvite"
        @copy="copyInvite"
    />

    <TopUpSheet
        :open="topUpOpen"
        :network-id="topUpNetworkId"
        :api="topUpApi ?? api"
        :telegram="tg"
        @close="topUpOpen = false"
        @paid="onTopUpPaid"
    />

    <SessionSheet
        v-if="target?.kind === 'computer'"
        :open="sessionSheetOpen"
        :target="target"
        :busy="busy"
        :refusal="sessionRefusal"
        @close="declineSession"
        @confirm="confirmSession"
    />

    <PreviewScanner
        v-if="previewMode && previewScannerOpen"
        :close-scanner="() => (previewScannerOpen = false)"
        :handle-detected="acceptPreviewScan"
    />
</template>
